import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    type CreateLabelsMutation,
    type CreateLabelsMutationVariables,
    CreateLabelsDocument,
} from '../../../api/mutations/createLabels';
import useLoadingButton from '../../../components/button/useLoadingButton';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import Form from '../../../components/fields/Form';
import NoItemResult from '../../../components/results/NoItemResult';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import LabelForm from '../LabelsDetailsPage/LabelForm';
import { type LabelFormikValue } from '../LabelsDetailsPage/LabelsDetailsInnerPage';
import useLabelsValidator from '../LabelsDetailsPage/useValidators';

const initialValues: LabelFormikValue = {
    backgroundColor: null,
    isActive: false,
    labelName: { defaultValue: '', overrides: [] },
    textColor: null,
    moduleId: null,
    moduleIds: [],
    moduleVariants: [],
};

const AddLabelsPage = () => {
    const { t } = useTranslation(['labelsList', 'labelDetails']);
    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();
    const validate = useLabelsValidator();
    const apolloClient = useApolloClient();
    const company = useCompany(true);

    const onSubmit = useHandleError<LabelFormikValue>(
        async value => {
            setLoading(true);
            // submitting message
            message.loading({
                content: t('labelDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { moduleId, moduleIds, ...valueSetting } = value;
            const settings = {
                ...pick(['backgroundColor', 'textColor', 'labelName', 'moduleVariants', 'isActive'], valueSetting),
            };

            await apolloClient
                .mutate<CreateLabelsMutation, CreateLabelsMutationVariables>({
                    mutation: CreateLabelsDocument,
                    variables: {
                        moduleId,
                        settings,
                    },
                })
                .finally(() => {
                    setLoading(false);
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('labelDetails:messages.createSuccessful'),
                key: 'primary',
            });

            navigate('/admin/labels');
        },
        [apolloClient, navigate, setLoading, t]
    );

    if (!company || !company.availableModules.hasLabelsModules) {
        return (
            <ConsolePageWithHeader onBack={() => navigate('/admin/labels')} title={t('labelDetails:addTitle')}>
                <NoItemResult
                    subTitle={
                        company && !company.availableModules.hasLabelsModules
                            ? t('labelsList:moduleNotAvailable')
                            : t('labelDetails:messages.noCompanySelectedWhenCreate')
                    }
                />
                ;
            </ConsolePageWithHeader>
        );
    }

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="labelForm" htmlType="submit" type="primary">
                    {t('labelDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/labels')}
            title={t('labelDetails:addTitle')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="labelForm" name="labelForm" onSubmitCapture={handleSubmit}>
                        <LabelForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddLabelsPage;
